package dev.danko.roblox_craft_ai.uikit.i18n

import dev.danko.roblox_craft_ai.shared.model.AssetType

interface AppStrings {
    val common: CommonStrings
    val assetTypes: AssetTypeStrings
    val errors: ErrorStrings
    val promptBox: PromptBoxStrings
    val assetCard: AssetCardStrings
    val pipeline: PipelineStrings
    val chat: ChatStrings
    val code: CodeStrings
    val meshViewer: MeshViewerStrings
    val header: HeaderStrings
    val home: HomeStrings
    val presetDialog: PresetDialogStrings
    val queue: QueueStrings
    val concept: ConceptStrings
    val health: HealthStrings
    val sessionToken: SessionTokenStrings
    val syncModal: SyncModalStrings
    val revisions: RevisionStrings
    val settingsRedirect: SettingsRedirectStrings
    val settingsPage: SettingsPageStrings
    val providerCard: ProviderCardStrings
    val presetsPage: PresetsPageStrings
    val nav: NavStrings
    val report: ReportStrings
    val auth: AuthStrings
    val billing: BillingStrings
    val admin: AdminStrings
    val panel: AssetPanelStrings
    val tour: TourStrings
    val legal: LegalStrings
    val access: AccessStrings
    val accountData: AccountDataStrings
    val meshInspector: MeshInspectorStrings
    val quality: QualityStrings
    val animationSetup: AnimationSetupStrings
}

/** Footer, legal pages and consent lines next to sign-in and checkout. */
interface LegalStrings {
    val terms: String
    val privacy: String
    val refunds: String
    val notAffiliated: String
    val loginConsent: String
    val checkoutConsent: String
    val backToSite: String
}

interface TourStrings {
    val step1Title: String
    val step1Text: String
    val step2Title: String
    val step2Text: String
    val step3Title: String
    val step3Text: String
    val next: String
    val back: String
    val start: String
    val skip: String
    fun stepOf(step: Int, total: Int): String
}

interface AssetPanelStrings {
    val tabCode: String
    val tabChat: String
    val tabVersions: String
    val tabRequest: String
    val tabFiles: String
    val tab3d: String
    val tabPrep: String
    val tabSketch: String
    val tabAnimation: String
    val downloadGlb: String
    val downloadLua: String
    val validationOk: String
    val technical: String
    val whatToChange: String
    val change: String
    val currentVersion: String
    val references: String
    val noVersions: String
    fun version(n: Int): String
    fun validationIssues(n: Int): String
}

interface AdminStrings {
    val nav: String
    val pageTitle: String
    val title: String
    val tabOverview: String
    val tabPlans: String
    val tabPacks: String
    val tabUsers: String
    val tabModeration: String
    val tabAudit: String
    val days7: String
    val days30: String
    val totalCost: String
    val anonymousCost: String
    val topUsers: String
    val byDay: String
    val events: String
    val noData: String
    val name: String
    val monthlyCredits: String
    val credits: String
    val price: String
    val currency: String
    val paddlePriceId: String
    val active: String
    val unlimitedHint: String
    val save: String
    val saved: String
    val priceWarning: String
    val addPack: String
    val packId: String
    val searchUsers: String
    val email: String
    val plan: String
    val creditsMonth: String
    val purchased: String
    val cost30d: String
    val status: String
    val registered: String
    val lastLogin: String
    val creditsDelta: String
    val note: String
    val grant: String
    val granted: String
    val block: String
    val unblock: String
    val statusChanged: String
    val noUsers: String
    val onlyOpen: String
    val resolve: String
    val resolved: String
    val kind: String
    val category: String
    val excerpt: String
    val noIncidents: String
    val whenLabel: String
    val admin: String
    val action: String
    val target: String
    val day: String
    val user: String
    val cost: String
    val providers: String
    val details: String
    val totalEvents: String
    fun saveFailed(reason: String): String
}

interface BillingStrings {
    val navPricing: String
    val pageTitle: String
    val title: String
    val subtitle: String
    val perMonth: String
    fun creditsPerMonth(credits: Int): String
    val unlimitedCredits: String
    val currentPlan: String
    val subscribe: String
    val switchPlan: String
    val free: String
    val packsTitle: String
    val packsHint: String
    fun packCredits(credits: Int): String
    val buy: String
    val manageTitle: String
    val cancelSubscription: String
    val updatePayment: String
    fun renewsOn(date: String): String
    val pastDueHint: String
    val paymentSoon: String
    val signInToBuy: String
    val checkoutDone: String
    val checkoutFailed: String
    val creditCostsTitle: String
    fun balance(monthlyLeft: String, purchased: Int): String
}

interface AuthStrings {
    val signIn: String
    val signOut: String
    val signedOut: String
    val accountTooltip: String
    val dialogTitle: String
    val dialogHint: String
    val emailLabel: String
    val emailPlaceholder: String
    val sendLink: String
    val sentTitle: String
    fun sentHint(email: String): String
    val useAnotherEmail: String
    val invalidEmail: String
    val throttled: String
    val sendFailed: String
    val verifyTitle: String
    val verifying: String
    fun verifySuccess(email: String): String
    val verifyFailed: String
    val requestNewLink: String
    val toWorkshop: String
    fun creditsTooltip(left: Int, limit: Int, purchased: Int, resetsOn: String): String
}

interface ReportStrings {
    val button: String
    val title: String
    val hint: String
    val reasonLabel: String
    val reasonInappropriate: String
    val reasonUnsafeCode: String
    val reasonCopyright: String
    val reasonOther: String
    val commentLabel: String
    val commentPlaceholder: String
    val submit: String
    val thanks: String
    val failed: String
}

interface CommonStrings {
    val cancel: String
    val save: String
    val delete: String
    val retry: String
    val regenerate: String
    val close: String
    val clear: String
    val copy: String
    val copied: String
    val confirm: String
    val send: String
    val show: String
    val hide: String
    val help: String
    val selectPlaceholder: String
    val demoBadge: String
    val betaBadge: String
    val you: String
    val language: String
    val simpleMode: String
    val expertMode: String
    val simpleModeHint: String
    fun lines(count: Int): String
    fun secondsShort(seconds: Int): String
    fun minutesSeconds(minutes: Int, seconds: Int): String
    fun agoSeconds(seconds: Int): String
    fun agoMinutes(minutes: Int): String
}

interface AssetTypeStrings {
    fun name(type: AssetType): String
    fun generateButton(type: AssetType): String
    fun availabilityNote(type: AssetType): String?
}

interface ErrorStrings {
    val aiUnavailable: String
    val rateLimited: String
    val network: String
    val meshTimeout: String
    val meshFailed: String
    val cancelled: String
    val generic: String
    val details: String
    val backendDownTitle: String
    val backendDownHint: String
    val checkAgain: String
    val contentBlocked: String
    val unsafeCodeBlocked: String
    val authRequired: String
    val quotaExceeded: String
    val budgetExceeded: String
}

interface PromptBoxStrings {
    val label: String
    /** Four view slots of the 3D tab. */
    val viewsTitle: String
    val viewFront: String
    val viewBack: String
    val viewLeft: String
    val viewRight: String
    val viewsHint: String
    val viewsSplitting: String
    fun viewsSplit(count: Int): String
    val viewsSwapSides: String
    val viewsHighOnlyFront: String
    val viewsSwitchToMax: String
    val viewsTooMany: String
    val attachReference: String
    fun addMorePhotos(count: Int): String
    val placeholder: String
    val referencesLabel: String
    val assetTypeLabel: String
    val aiModelLabel: String
    val autoProvider: String
    val generating: String
    val tooManyReferences: String
    val onlyImages: String
    val fileTooLarge: String
    val dropTitle: String
    val dropHint: String
    val dropAria: String
    val emptyPrompt: String
    fun promptFromImages(type: AssetType): String
}

interface AssetCardStrings {
    val stagePrompt: String
    val stageAi: String
    val stageBuild: String
    val stageSync: String
    val stageDone: String
    val statusPending: String
    val statusValidating: String
    val statusRetrying: String
    val statusStreaming: String
    val statusReady: String
    val statusFailed: String
    fun revisionBadge(current: Int, total: Int): String
    fun referencesBadge(count: Int): String
    fun syncPending(countdown: String): String
    fun syncDelivering(countdown: String): String
    val syncSynced: String
    val syncFailed: String
    val deleteTooltip: String
    val waitingForPlugin: String
    val progressValidating: String
    val progressRetrying: String
    fun progressTokens(count: Int): String
    val progressProcessing: String
    val download: String
    val editPrompt: String
    val aiChat: String
    val downloadZip: String
    val synced: String
    val retrySync: String
    val syncToStudio: String
    val demoMeshHint: String
    val betaAnimationHint: String
}

interface PipelineStrings {
    val prompt: String
    val luauPromptDescription: String
    val llmStream: String
    val llmStreamDescription: String
    val syntaxCheck: String
    val syntaxCheckDescription: String
    val ready: String
    val luauReadyDescription: String
    val meshPromptDescription: String
    val meshGeneration: String
    val meshGenerationDescription: String
    val meshTexturing: String
    val meshTexturingDescription: String
    val meshExport: String
    val meshExportDescription: String
    val meshReadyDescription: String
    val animationPromptDescription: String
    val motionAi: String
    val motionAiDescription: String
    val rigging: String
    val riggingDescription: String
    val animationReadyDescription: String
    val scaffoldTree: String
    val scaffoldTreeDescription: String
    val serverServices: String
    val serverServicesDescription: String
    val clientControllers: String
    val clientControllersDescription: String
    val rojoReady: String
    val rojoReadyDescription: String
}

interface ChatStrings {
    val quickOptimize: String
    val quickStrict: String
    val quickBugs: String
    val quickErrors: String
    val quickDocs: String
    fun revisionShort(number: Int): String
    fun revisionLong(number: Int): String
    val inputPlaceholder: String
    val aiAssistant: String
    val diffTitle: String
    val acceptChanges: String
    val thinking: String
    val changesApplied: String
}

interface CodeStrings {
    val run: String
    val runViaBackend: String
    val connectPluginFirst: String
    fun projectTree(files: Int): String
    val filterPlaceholder: String
    val undoTooltip: String
    val redoTooltip: String
    val resetTooltip: String
    val copyTooltip: String
    val downloadTooltip: String
    val fullscreenTooltip: String
}

interface MeshViewerStrings {
    val shadingTextured: String
    val shadingClay: String
    val shadingWire: String
    val shadingFlat: String
    val lightStudio: String
    val lightSun: String
    val lightNeon: String
    val cameraIso: String
    val cameraFront: String
    val cameraTop: String
    val cameraSide: String
    val pause: String
    val spin: String
    val exitFullscreen: String
    val fullscreen: String
    fun stats(triangles: String, vertices: String, dimensions: String): String
    val controlsHint: String
    val screenshot: String
    val screenshotTitle: String
    fun screenshotSaved(filename: String): String
    val animationParseError: String
    val animationMissing: String
    val animationPreviewTitle: String
    val play: String
    val stop: String
    val loopOn: String
    val loopOff: String
    val exportKeyframes: String
    fun resizeHint(heightPx: Int): String
    val shadingBaseColor: String
    val shadingNormals: String
    val shadingUvChecker: String
    val shadingXray: String
    val lightRoblox: String
    val lightNight: String
}

interface HeaderStrings {
    val appTitle: String
    val appSubtitle: String
    val presets: String
    val settings: String
    fun aiReady(count: Int): String
    val aiDemo: String
    val pluginConnected: String
    val pluginPending: String
    val tokenLabel: String
}

interface HomeStrings {
    val searchWorks: String
    val allIdeas: String
    val open: String
    val moreActions: String
    val generateSimilar: String
    val options: String
    val sendToStudio: String
    fun creditsCost(credits: Int): String
    val rename: String
    val renameFailed: String
    val noMatches: String
    val pageTitle: String
    val heroTitle: String
    val heroSubtitle: String
    val samplePrompt: String
    val savePreset: String
    val addToQueue: String
    val conceptMode: String
    val shiftHint: String
    val noIdea: String
    val browsePresets: String
    val recentAssets: String
    fun totalAssets(count: Int): String
    val wideView: String
    val gridView: String
    val noAssetsTitle: String
    val noAssetsHint: String
    fun generationFailed(reason: String): String
    val cancelledByUser: String
    val conceptGenerating: String
    val conceptReady: String
    fun conceptFailed(reason: String): String
    val conceptApproved: String
    fun meshFromConceptFailed(reason: String): String
    val conceptRerolling: String
    val conceptRerolled: String
    fun conceptRerollFailed(reason: String): String
    fun presetLoaded(title: String): String
    fun queued(pending: Int, position: Int): String
    val enterPromptBeforeSaving: String
    val personalPresetDescription: String
    fun presetSaved(title: String): String
    val presetSaveFailed: String
    fun cardDeleted(shortId: String): String
    val regenerating: String
    fun queuedForStudio(shortId: String): String
    val syncBackendUnavailable: String
    val settingsSaved: String
    fun refactorFailed(reason: String): String
    val changesApplied: String
    fun applyChangesFailed(reason: String): String
}

interface PresetDialogStrings {
    val title: String
    val nameLabel: String
    val namePlaceholder: String
    val nameTooShort: String
    fun nameCounter(length: Int): String
    val descriptionLabel: String
    val descriptionPlaceholder: String
    val tagsLabel: String
    val tagsPlaceholder: String
    fun tagsCounter(count: Int): String
    val currentPrompt: String
    val emptyPrompt: String
    fun typeAndLimit(typeName: String): String
}

interface QueueStrings {
    val statusPending: String
    val statusRunning: String
    val statusDone: String
    val statusFailed: String
    val statusCancelled: String
    fun title(size: Int): String
    fun eta(minutes: Int, seconds: Int): String
    val resume: String
    val pause: String
    val resumed: String
    val paused: String
    val pausedBadge: String
    val cleared: String
    val empty: String
    val scrollTip: String
    val taskCancelled: String
    fun position(position: Int): String
    val startCallbackMissing: String
    val generationFailed: String
    val unexpectedError: String
}

interface ConceptStrings {
    val stepTitle: String
    val stepHint: String
    val savingBadge: String
    val imageAlt: String
    fun promptExpansion(expanded: Boolean): String
    val reconstructing: String
    val approve: String
    val regenerating: String
    val regenerate: String
}

interface HealthStrings {
    val unknown: String
    val allOk: String
    val critical: String
    fun degraded(component: String): String
    val degradedGeneric: String
    val title: String
    fun updatedAt(time: String): String
}

interface SessionTokenStrings {
    val validationFailed: String
    val title: String
    val saveAndLink: String
    val instructions: String
    val tokenLabel: String
    val tokenPlaceholder: String
    val pasteFromClipboard: String
    val generateNew: String
}

interface SyncModalStrings {
    val untitledAsset: String
    val title: String
    val confirm: String
    val warning: String
    val averageDelay: String
    val dontShowAgain: String
}

interface RevisionStrings {
    val title: String
    val listLabel: String
    fun restore(number: Int): String
    fun restored(number: Int): String
    fun latest(number: Int): String
}

interface SettingsRedirectStrings {
    val deprecatedNotice: String
    val redirecting: String
    val openingNewPage: String
    val movedNotice: String
    val openSettings: String
}

interface SettingsPageStrings {
    val tabAccount: String
    val signedOutTitle: String
    val signedOutHint: String
    val advanced: String
    val dangerZone: String
    val resetSessionHint: String
    val pageTitle: String
    val subtitle: String
    val hashLabel: String
    val backToStudio: String
    val cabinetTitle: String
    val cabinetSubtitle: String
    val resetSession: String
    val resetSessionConfirm: String
    val sessionReset: String
    val tabKeys: String
    val tabGeneral: String
    val tabPlugin: String
    val tabAssets: String
    val keyAdded: String
    val keyAddFailed: String
    val keyUpdated: String
    val keyUpdateFailed: String
    val keyDeleted: String
    val keyDeleteFailed: String
    val priorityChanged: String
    val priorityChangeFailed: String
    val settingsSaved: String
    val saveFailed: String
    val resetDone: String
    val resetFailed: String
    val pluginSettingsSaved: String
    val connectionOkEmpty: String
    fun connectionOkQueued(count: Int): String
    val connectionFailed: String
    val assetRemovedFromCache: String
    val keysTitle: String
    val keysManagedByServerTitle: String
    val keysManagedByServerHint: String
    fun keysSummary(total: Int, active: Int): String
    val refresh: String
    val catalogMissing: String
    val catalogMissingHint: String
    val providersHeader: String
    val noModels: String
    val generalTitle: String
    val defaultModel: String
    val selectModel: String
    val defaultAssetType: String
    val skipSyncConfirm: String
    val skipSyncConfirmHint: String
    val themeSystem: String
    val themeLight: String
    val themeDark: String
    val themeLabel: String
    val languageLabel: String
    val reset: String
    val pluginSettingsTitle: String
    val pluginTokenTitle: String
    val tokenCopied: String
    val sessionHashTitle: String
    val pluginPort: String
    val pluginPortHint: String
    val autoConnect: String
    val autoConnectHint: String
    val studioPath: String
    val testConnection: String
    val howToConnectTitle: String
    val howToConnectSteps: List<String>
    val downloadPlugin: String
    val myAssetsTitle: String
    fun assetsSummary(total: Int, shown: Int): String
    val filterAll: String
    val filterReady: String
    val filterProcessing: String
    val filterFailed: String
    val deselectAll: String
    val selectAll: String
    fun export(selected: Int): String
    fun deleteSelected(count: Int): String
    fun deleteAssetsConfirm(count: Int): String
    val deleted: String
    val noAssets: String
    val noAssetsHint: String
    val goToStudio: String
}

interface ProviderCardStrings {
    val defaultLabel: String
    fun type(name: String): String
    val active: String
    val notConfigured: String
    fun addKeyFor(provider: String): String
    fun addKeyTitle(provider: String): String
    val labelField: String
    val labelPlaceholder: String
    val apiKeyField: String
    val apiKeyPlaceholder: String
    fun keyPatternWarning(provider: String): String
    val keyTooShort: String
    val baseUrlOptional: String
    fun baseUrlDefault(url: String): String
    val test: String
    val getApiKey: String
    fun editKeyTitle(provider: String): String
    val labelShort: String
    val baseUrlEmptyDefault: String
    val keyHiddenNotice: String
    fun deleteKeyTitle(label: String): String
    val deleteIrreversible: String
    fun providerKeyLine(provider: String, key: String): String
    fun secondsLeft(seconds: Int): String
}

interface PresetsPageStrings {
    val pageTitle: String
    val subtitle: String
    val toStudio: String
    val categories: String
    val categoryAll: String
    val categoryEnvironment: String
    val categoryNpc: String
    val categoryInterior: String
    val categoryWeapon: String
    val categoryItem: String
    val categoryAnimation: String
    val categoryScript: String
    val categoryPersonal: String
    val categoryOther: String
    val allCategories: String
    val difficultyEasy: String
    val difficultyMedium: String
    val difficultyHard: String
    val searchPlaceholder: String
    fun found(count: Int): String
    val noPersonalTitle: String
    val noPersonalHint: String
    val goToStudio: String
    fun nothingFound(query: String): String
    val nothingFoundHint: String
    val deleteTitle: String
    fun deleted(title: String): String
    fun deleteConfirm(title: String): String
    val usePrompt: String
}

interface NavStrings {
    val workshop: String
    val menu: String
    val on: String
    val off: String
    fun plan(name: String): String
    val studioConnected: String
    val studioNotConnected: String
    val home: String
    val about: String
    val toggleColorMode: String
    val builtWith: String
    val templateBy: String
}

object FriendlyErrors {
    private val NETWORK_ERROR_MARKERS = listOf(
        "Network error",
        "Failed to fetch",
        "Fail to fetch",
        "failed to reach",
        "NetworkError",
        "Load failed",
        "ERR_CONNECTION_REFUSED",
        "Connection refused"
    )

    fun describe(raw: String?, strings: AppStrings): String {
        val message = raw.orEmpty()
        val errors = strings.errors
        return when {
            message.contains("AUTH_REQUIRED") -> errors.authRequired
            message.contains("QUOTA_EXCEEDED") -> errors.quotaExceeded
            message.contains("BUDGET_EXCEEDED") -> errors.budgetExceeded
            message.contains("CONTENT_BLOCKED:UNSAFE_CODE") -> errors.unsafeCodeBlocked
            message.contains("CONTENT_BLOCKED") -> errors.contentBlocked
            message.contains("cancel", ignoreCase = true) || message.contains("отмен", ignoreCase = true) -> errors.cancelled
            message.contains("RATE_LIMIT", ignoreCase = true) || message.contains("429") || message.contains("Лимит запросов") -> errors.rateLimited
            message.contains("Timed out", ignoreCase = true) -> errors.meshTimeout
            message.contains("3D Mesh generation failed", ignoreCase = true) -> errors.meshFailed
            message.contains("FailoverChain", ignoreCase = true) || message.contains("ChainFailed") || message.contains("AiProvider", ignoreCase = true) -> errors.aiUnavailable
            NETWORK_ERROR_MARKERS.any { message.contains(it, ignoreCase = true) } -> errors.network
            else -> errors.generic
        }
    }
}

/** Promo page and access requests of the invite-only phase. */
interface AccessStrings {
    val badge: String
    val title: String
    val subtitle: String
    val f1Title: String
    val f1Text: String
    val f2Title: String
    val f2Text: String
    val f3Title: String
    val f3Text: String
    val formTitle: String
    val formHint: String
    val nameLabel: String
    val namePlaceholder: String
    val messageLabel: String
    val messagePlaceholder: String
    val ageConfirm: String
    val privacyAccept: String
    val submit: String
    val sentTitle: String
    fun sentText(email: String): String
    val alreadyApproved: String
    val signIn: String
    val notApproved: String
    val consentRequired: String
    val sendFailed: String
    val adminTab: String
    val pending: String
    val approved: String
    val rejected: String
    val all: String
    val approve: String
    val reject: String
    val noRequests: String
    val approvedToast: String
    val rejectedToast: String
}

/** "Your data" in Settings → Account (export, deletion) and account deletion in the admin panel. */
interface AccountDataStrings {
    val title: String
    val hint: String
    val download: String
    val delete: String
    val deleteTitle: String
    val deleteWarning: String
    val confirmLabel: String
    val confirmButton: String
    val emailMismatch: String
    val subscriptionActive: String
    val deleted: String
    val failed: String
    val adminDelete: String
    val adminDeleted: String
    fun adminConfirm(email: String): String
}

/** Info, view and tools panel under the 3D model viewer. */
interface MeshInspectorStrings {
    fun number(value: Int): String
    val reliefMap: String
    val reliefYes: String
    val reliefNo: String
    val relief: String
    val reliefHint: String
    val tabInfo: String
    val tabView: String
    val tabTools: String
    fun studs(value: Double): String
    val readinessTitle: String
    val readyAll: String
    fun readyIssues(count: Int): String
    fun checkTriangles(count: Int, max: Int): String
    val checkTrianglesOver: String
    fun checkTexture(size: Int): String
    val checkTextureNone: String
    val checkTextureOver: String
    val checkFrontAuto: String
    val checkFrontUser: String
    val checkFrontUnsure: String
    fun checkSize(dimensions: String): String
    val checkSizeTiny: String
    val checkSizeHuge: String
    val checkPartsOk: String
    fun checkParts(small: Int): String
    val checkPivotOk: String
    val checkPivotOff: String
    val checking: String
    val fixSimplify: String
    val fixTexture: String
    val fixRotate: String
    val fixSize: String
    val fixDebris: String
    val fixPivot: String
    val geometry: String
    val triangles: String
    val vertices: String
    val parts: String
    val size: String
    val fileSize: String
    fun megabytes(bytes: Long): String
    val texture: String
    fun pixels(size: Int): String
    val noTexture: String
    val origin: String
    val provider: String
    val created: String
    val version: String
    val sketch: String
    val mode: String
    val light: String
    val background: String
    val bgDark: String
    val bgLight: String
    val bgGrey: String
    val bgBaseplate: String
    val reference: String
    val referenceHint: String
    val bounds: String
    val grid: String
    val spin: String
    val screenshot: String
    val screenshotTransparent: String
    val video: String
    val videoRecording: String
    val videoUnsupported: String
    val toolsNote: String
    val budget: String
    val budgetMobile: String
    val budgetNormal: String
    val budgetDetailed: String
    fun trianglesValue(count: Int): String
    val apply: String
    val sizeTitle: String
    val sizeHint: String
    val rotateTitle: String
    val rotateHint: String
    val rotateLeft: String
    val rotateRight: String
    val rotate180: String
    val textureTitle: String
    val debrisTitle: String
    val debrisHint: String
    val downloadTitle: String
    val downloadGlb: String
    val downloadObj: String
    val versions: String
    fun versionLabel(number: Int): String
    val current: String
    val restore: String
    val compare: String
    val closeCompare: String
    val originalVersion: String
    fun editTriangles(count: Int): String
    val editHighPoly: String
    /** The detailed source is still kept: the budget can go up and the high-poly model can be downloaded. */
    fun budgetFromSource(sourceTriangles: Int?): String
    val downloadHighPoly: String
    val downloadSharp: String
    /** Skeleton for animations. */
    val rigTitle: String
    val rigHint: String
    val rigApply: String
    val rigDone: String
    val rigDownload: String
    val rigWorking: String
    fun rigPro(cost: String?): String
    val rigProHint: String
    val rigByMeshy: String
    val preparingSharp: String
    /** «Сменить текстуру»: a new style for the finished model. */
    val retextureTitle: String
    val retextureHint: String
    val retexturePresets: List<String>
    val retexturePlaceholder: String
    val retexturePicture: String
    val retextureRemovePicture: String
    fun retextureApply(cost: String?): String
    val retextureWorking: String
    val editRetexture: String
    fun editSize(studs: String): String
    fun editRotate(degrees: Int): String
    fun editTexture(size: Int): String
    val editDebris: String
    val working: String
    fun saved(number: Int): String
    val failed: String
    val unavailable: String
}

/** Choice between cheaper and better generation for sketches and 3D models. */
interface QualityStrings {
    val sketchTitle: String
    val meshTitle: String
    val sketchStandard: String
    val sketchStandardHint: String
    val sketchHigh: String
    val sketchHighHint: String
    val meshStandard: String
    val meshStandardHint: String
    val meshHigh: String
    val meshHighHint: String
    val meshMax: String
    val meshMaxHint: String
    val note: String
    fun approve(cost: String): String
    val drewCheaper: String
    val variantsTitle: String
    val variantsOne: String
    val variantsOneHint: String
    val variantsFour: String
    val variantsFourHint: String
    val pickVariant: String
    fun variantLabel(number: Int): String
    /** Triangle target chosen before a 3D model is made. */
    val trianglesTitle: String
    val trianglesMob: String
    val trianglesCharacter: String
    val trianglesBoss: String
    val trianglesHighPoly: String
    val trianglesHighPolyHint: String
    val trianglesCustom: String
    fun trianglesUpTo(count: Int): String
    val trianglesNote: String
    /** Texture the generator paints, chosen before a 3D model is made. */
    val textureTitle: String
    val texture1kHint: String
    val texture2kHint: String
    val texture4kHint: String
    val textureNote: String
}

/** The animation settings panel: character, combat stance, weapons and grip; and the animation workspace. */
interface AnimationSetupStrings {
    val characterTitle: String
    val characterR15: String
    val characterR15Hint: String
    val characterCustom: String
    val characterCustomHint: String
    val characterOwnHint: String
    val modeTitle: String
    val modeNone: String
    val modeNoneHint: String
    val modeFists: String
    val modeFistsHint: String
    val modeOne: String
    val modeOneHint: String
    val modeTwo: String
    val modeTwoHint: String
    val weaponTitle: String
    val offhandTitle: String
    val sword: String
    val gun: String
    val staff: String
    val axe: String
    fun weaponHint(weapon: String): String
    val weaponModelTitle: String
    val weaponModelBuiltIn: String
    val weaponModelBuiltInHint: String
    val weaponModelOwnHint: String
    val gripTitle: String
    val gripOne: String
    val gripOneHint: String
    val gripTwo: String
    val gripTwoHint: String
    val note: String
    val noteWithWeapon: String
    // Workspace
    val openWorkspace: String
    val closeWorkspace: String
    val clipTitle: String
    val duration: String
    val looped: String
    val once: String
    val keys: String
    val jointsTitle: String
    val prevKey: String
    val nextKey: String
    val zoomIn: String
    val zoomOut: String
    val resetCamera: String
    val timeZoom: String
    val cameraHint: String
    val head: String
    val torso: String
    val leftArm: String
    val rightArm: String
    val leftLeg: String
    val rightLeg: String
    // Changing a finished animation in words
    val editTitle: String
    val editHint: String
    val editPresets: List<String>
    val editPlaceholder: String
    fun editApply(cost: String?): String
    val editWorking: String
    fun editDone(version: Int): String
    val editFailed: String
    val versionsTitle: String
    val versionOriginal: String
    val restore: String
    // The model window: choosing, uploading and fixing the character and weapon models
    val libraryOpen: String
    val libraryTitle: String
    val libraryDone: String
    val libraryCharacterHint: String
    val libraryWeaponHint: String
    val libraryNoBones: String
    val libraryEmpty: String
    val libraryAddBones: String
    val libraryAddingBones: String
    val libraryTools: String
    val libraryDownload: String
    val libraryUpload: String
    val libraryUploadHint: String
    val libraryUploading: String
    val libraryFailed: String
    fun libraryImported(name: String): String
    // Weapons per hand
    val weaponTitleOne: String
    val rightHandTitle: String
    val leftHandTitle: String
    val handTitle: String
    val handLeadTitle: String
    val handRight: String
    val handLeft: String
    val handRightHint: String
    val handLeftHint: String
    fun weaponMovesLike(kind: String): String
}
